import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

function getErrorDetail(err) {
  return err.response?.data?.detail;
}

function getDetailCode(err) {
  const detail = getErrorDetail(err);
  return detail && typeof detail === "object" ? detail.code : null;
}

function getDetailMessage(err) {
  const detail = getErrorDetail(err);
  if (detail && typeof detail === "object" && detail.message) return detail.message;
  return null;
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);
  const [statusNotice, setStatusNotice] = useState(null); // { type, message }
  const [resending, setResending] = useState(false);
  const { login, resendVerification } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setUnverifiedEmail(null);
    setStatusNotice(null);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      const code = getDetailCode(err);
      if (code === "email_not_verified") {
        setUnverifiedEmail(email);
        toast.error("Please confirm your email to sign in.");
      } else if (code === "account_pending_approval") {
        setStatusNotice({ type: "pending", message: getDetailMessage(err) });
        toast.error("Your account is pending approval.");
      } else if (code === "account_rejected") {
        setStatusNotice({ type: "rejected", message: getDetailMessage(err) });
        toast.error("Account not approved.");
      } else {
        toast.error(formatApiErrorDetail(getErrorDetail(err)) || "Login failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!unverifiedEmail) return;
    setResending(true);
    try {
      await resendVerification(unverifiedEmail);
      toast.success("Confirmation email sent. Check your inbox.");
    } catch (_e) {
      toast.error("Could not resend right now. Try again in a moment.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div data-testid="login-page" className="px-6 md:px-12 lg:px-24 py-24 max-w-md mx-auto">
      <p className="label-eyebrow text-[#555]">— Sign In</p>
      <h1 className="font-serif-display text-5xl tracking-tighter leading-none font-light mt-4">
        Welcome back
      </h1>
      <form onSubmit={handleSubmit} className="mt-12 space-y-8">
        <div>
          <label className="label-eyebrow text-[#555]">Email</label>
          <Input
            data-testid="login-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-[#111]"
          />
        </div>
        <div>
          <label className="label-eyebrow text-[#555]">Password</label>
          <Input
            data-testid="login-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-[#111]"
          />
        </div>
        <Button
          type="submit"
          data-testid="login-submit"
          disabled={submitting}
          className="w-full rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white py-6 h-auto label-eyebrow"
        >
          {submitting ? "Signing in…" : "Sign In"}
        </Button>
        <p className="text-right text-sm">
          <Link
            data-testid="login-forgot-link"
            to="/forgot-password"
            className="text-[#555] hover:text-[#1E3A8A] underline-offset-2 hover:underline"
          >
            Forgot password?
          </Link>
        </p>
      </form>

      {unverifiedEmail && (
        <div
          data-testid="login-unverified-banner"
          className="mt-8 border border-[#1E3A8A]/30 bg-[#F4EFE6]/40 p-5"
        >
          <p className="label-eyebrow text-[#1E3A8A]">— Email not confirmed</p>
          <p className="text-sm text-[#333] mt-2">
            We sent a confirmation link to{" "}
            <span className="font-mono-aam text-[#1E3A8A]">{unverifiedEmail}</span>. Didn't get it?
          </p>
          <Button
            onClick={handleResend}
            disabled={resending}
            data-testid="login-resend-verification-btn"
            className="mt-4 rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white px-5 py-3 h-auto label-eyebrow"
          >
            {resending ? "Sending…" : "Resend confirmation email"}
          </Button>
        </div>
      )}

      {statusNotice && (
        <div
          data-testid={`login-${statusNotice.type}-banner`}
          className={`mt-8 border p-5 ${
            statusNotice.type === "rejected"
              ? "border-[#D32F2F]/40 bg-[#D32F2F]/5"
              : "border-[#F4C430]/60 bg-[#F4EFE6]/60"
          }`}
        >
          <p className={`label-eyebrow ${statusNotice.type === "rejected" ? "text-[#D32F2F]" : "text-[#A87900]"}`}>
            {statusNotice.type === "rejected" ? "— Account not approved" : "— Pending approval"}
          </p>
          <p className="text-sm text-[#333] mt-2">{statusNotice.message}</p>
        </div>
      )}

      <p className="mt-8 text-sm text-[#555]">
        No account?{" "}
        <Link to="/register" className="text-[#111] underline hover:text-[#1E3A8A]">
          Join AfricaArtMasters
        </Link>
      </p>
    </div>
  );
}
